import { iconDadosCorretores, iconDocAnalise, iconMarcaLogoPrevidencia, iconMarcaLogoSaude, iconMarcaLogoVida, iconNavConfig, iconNavHome, iconNavMensagem, iconNavSair, iconProdutosVb } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useId, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import packageJson from '../../../../package.json';
import { useAuth } from '../../hooks/useAuth';
import { useSystemContainer } from '../../hooks/useSystemContainer';
import type { ContentType } from '../../routes/routesConfig';
import { useCompanyStore, useRoleDisplay } from '../../store/companyStore';
import type { SidebarProps } from '../../types/componentType';
import {
  EMPRESA_DISPLAY_MAX_SIDEBAR,
  getSelectedCompanyNomeEstipulanteApol,
  getSelectedCompanyRamoLine,
  truncateEmpresaDisplayLabel,
} from '../../types/companyType';
import { apiService } from '../../services';
import { SystemMessage, SystemMessageConfirmation } from '../index';
import Visibility from '../visibility/Visibility';
import { SystemMessageType } from '../../types/componentType';

/** Aligned with apiService fetch timeout (30s). */
const WEB_VIDA_OPEN_TIMEOUT_MS = 30_000;
const WEB_VIDA_OPEN_TIMEOUT_MESSAGE =
  'Tempo limite excedido. Não foi possível abrir o Web Vida. Tente novamente.';

const normalizeText = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const PRODUCT_MENU_PATHS = new Set(['/vida', '/saude', '/previdencia']);

const isProductMenuPath = (path: string): boolean => PRODUCT_MENU_PATHS.has(path);

type SidebarMenuItem = {
  id: number;
  name: string;
  icon: React.ReactElement;
  content: ContentType;
  path: string;
  disabled?: boolean;
  hidden?: boolean;
  /** Opens existing Pega Portal do Estipulante (Web Vida) with auto-login in a new tab. */
  openWebVida?: boolean;
};

/** Hidden items in the UI; routes and definition remain for future reactivation. */
const HIDDEN_SIDEBAR_MENU_IDS = new Set([7, 8, 9]);

/** Itens visible but disabled in the UI. */
const DISABLED_SIDEBAR_MENU_IDS = new Set([5]);

const resolveSelectedLineOfBusiness = (ramo?: string): 'vida' | 'saude' | 'previdencia' | null => {
  if (!ramo || !ramo.trim()) {
    return null;
  }

  const normalizedRamo = normalizeText(ramo);
  if (normalizedRamo.includes('vida')) return 'vida';
  if (normalizedRamo.includes('saude')) return 'saude';
  if (normalizedRamo.includes('previdencia')) return 'previdencia';
  return null;
};

const Sidebar: React.FC<SidebarProps> = ({ isOpen = true, onToggle, currentContent = 'home', isLoggingOut = false }) => {
  const { user, logout } = useAuth();
  const roleDisplay = useRoleDisplay();
  const { selectedCompany, requestCompanyCardOpen } = useCompanyStore();
  const { setLoading } = useSystemContainer();
  const navigate = useNavigate();
  const [selectedItem, setSelectedItem] = useState<number>(1); // Start with Home selected
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showSelectPolicyMessage, setShowSelectPolicyMessage] = useState(false);
  const [webVidaErrorMessage, setWebVidaErrorMessage] = useState('');
  const [isOpeningWebVida, setIsOpeningWebVida] = useState(false);
  const userName = user?.pessoaFisicaResponse?.pessoaFisica?.nome;

  const sidebarId = useId();
  const userCardId = useId();
  const overlayId = useId();
  const selectedLineOfBusiness = useMemo(
    () => resolveSelectedLineOfBusiness(getSelectedCompanyRamoLine(selectedCompany)),
    [selectedCompany?.policySelection?.ramoNome, selectedCompany?.policySelection?.ramoCodigo],
  );
  const hasSelectedPolicy = Boolean(
    String(selectedCompany?.policySelection?.numeroApolice ?? '').trim(),
  );
  const empresaLabelFull = hasSelectedPolicy
    ? getSelectedCompanyNomeEstipulanteApol(selectedCompany) || '—'
    : '';
  const empresaLabelShort = truncateEmpresaDisplayLabel(empresaLabelFull, EMPRESA_DISPLAY_MAX_SIDEBAR);
  const apoliceNumeroFull = hasSelectedPolicy
    ? String(selectedCompany?.policySelection?.numeroApolice ?? '').trim()
    : '';
  const apoliceNumeroShort = truncateEmpresaDisplayLabel(apoliceNumeroFull, EMPRESA_DISPLAY_MAX_SIDEBAR);
  const productMenuIsEnabled = (product: 'vida' | 'saude' | 'previdencia') =>
    selectedLineOfBusiness !== null && selectedLineOfBusiness === product;

  const menuItems = useMemo<SidebarMenuItem[]>(() => [
    {
      id: 1,
      name: 'Home',
      icon: <BsdsIcon icon={iconNavHome} className="w-5 h-5" />,
      content: '/' as ContentType,
      path: '/home'
    },
    {
      id: 2,
      name: 'Saúde',
      icon: <BsdsIcon icon={iconMarcaLogoSaude} className="w-5 h-5" />,
      content: '/saude' as ContentType,
      path: '/saude',
      disabled: !productMenuIsEnabled('saude')
    },
    {
      id: 3,
      name: 'Vida',
      icon: <BsdsIcon icon={iconMarcaLogoVida} className="w-5 h-5" />,
      content: '/vida' as ContentType,
      path: '/vida',
      disabled: !productMenuIsEnabled('vida')
    },
    {
      id: 4,
      name: 'Previdência',
      icon: <BsdsIcon icon={iconMarcaLogoPrevidencia} className="w-5 h-5" />,
      content: '/previdencia' as ContentType,
      path: '/previdencia',
      disabled: !productMenuIsEnabled('previdencia')
    },
    {
      id: 5,
      name: 'Mensagem',
      icon: <BsdsIcon icon={iconNavMensagem} className="w-5 h-5" />,
      content: '/message' as ContentType,
      path: '/message',
      disabled: DISABLED_SIDEBAR_MENU_IDS.has(5),
    },
    {
      id: 6,
      name: 'WebVida',
      icon: <BsdsIcon icon={iconProdutosVb} className="w-5 h-5" />,
      content: '/webvida' as ContentType,
      path: '/webvida',
      openWebVida: true,
    },
    {
      id: 7,
      name: 'Gerenciamento',
      icon: <BsdsIcon icon={iconDadosCorretores} className="w-5 h-5" />,
      content: '/management' as ContentType,
      path: '/management',
      hidden: HIDDEN_SIDEBAR_MENU_IDS.has(7),
    },
    {
      id: 8,
      name: 'Relatórios',
      icon: <BsdsIcon icon={iconDocAnalise} className="w-5 h-5" />,
      content: '/reports' as ContentType,
      path: '/reports',
      hidden: HIDDEN_SIDEBAR_MENU_IDS.has(8),
    },
    {
      id: 9,
      name: 'Configurações',
      icon: <BsdsIcon icon={iconNavConfig} className="w-5 h-5" />,
      content: 'settings' as ContentType,
      path: '/settings',
      hidden: HIDDEN_SIDEBAR_MENU_IDS.has(9),
    },
  ], [productMenuIsEnabled]);

  const visibleMenuItems = useMemo(
    () => menuItems.filter((item) => !item.hidden),
    [menuItems],
  );

  // Synchronize the selected item with the current content
  useEffect(() => {
    const matchingItem = menuItems.find(item => item.content === currentContent);
    if (matchingItem) {
      setSelectedItem(matchingItem.id);
    } else {
      // If the current content doesn't match any menu item (e.g., userProfile),
      // deselect all items
      setSelectedItem(0);
    }
  }, [currentContent, menuItems]);

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    await logout();
    setShowLogoutModal(false);
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  const openWebVidaWithAutoLogin = async () => {
    if (isOpeningWebVida) {
      return;
    }

    const apolice = String(selectedCompany?.policySelection?.numeroApolice ?? '').trim();
    if (!apolice) {
      requestCompanyCardOpen();
      setShowSelectPolicyMessage(true);
      return;
    }

    const cpf = String(user?.cpf ?? '').replace(/\D/g, '');

    const nomePlain = String(userName ?? '').trim();
    if (!nomePlain) {
      setWebVidaErrorMessage('Nome do usuário não disponível para abrir o Web Vida.');
      return;
    }

    if (!cpf) {
      setWebVidaErrorMessage('CPF do usuário não disponível para abrir o Web Vida.');
      return;
    }

    setIsOpeningWebVida(true);
    setWebVidaErrorMessage('');
    setLoading(true);

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      const response = await Promise.race([
        apiService.portalEstipulante.createRedirect({
          cnpj: cpf,
          nome: nomePlain,
        }),
        new Promise<never>((_, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error(WEB_VIDA_OPEN_TIMEOUT_MESSAGE)),
            WEB_VIDA_OPEN_TIMEOUT_MS,
          );
        }),
      ]);

      if (!response.redirectUrl) {
        throw new Error('URL do Portal do Estipulante não retornada pelo servidor.');
      }

      window.open(response.redirectUrl, '_blank', 'noopener,noreferrer');
      onToggle?.();
    } catch (error) {
      const message = error instanceof Error && error.message
        ? error.message
        : 'Não foi possível abrir o Web Vida. Tente novamente.';
      setWebVidaErrorMessage(message);
    } finally {
      if (timeoutId !== undefined) {
        clearTimeout(timeoutId);
      }
      setIsOpeningWebVida(false);
      setLoading(false);
    }
  };

  const handleMenuItemClick = (item: (typeof menuItems)[number], isDisabled: boolean) => {
    if (isDisabled) {
      return;
    }

    if (item.openWebVida) {
      void openWebVidaWithAutoLogin();
      return;
    }

    if (isProductMenuPath(item.path) && !hasSelectedPolicy) {
      requestCompanyCardOpen();
      setShowSelectPolicyMessage(true);
      return;
    }

    setSelectedItem(item.id);
    navigate(item.path);
    onToggle?.();
  };

  return (
    <>
      {/* Overlay/Dark mask for mobile */}
      {isOpen && (
        <div
          id={overlayId}
          className="fixed inset-0 bg-black/55 bg-opacity-50 z-55 lg:hidden"
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              onToggle?.();
            }
          }}
          role="button"
          tabIndex={0}
          aria-label="Fechar menu de navegação"
          aria-hidden={isOpen ? 'false' : 'true'}
        />
      )}

      <aside
        id={sidebarId}
        className={`fixed left-0 z-60 lg:z-0 w-72 bg-background-secondary rounded-tr-lg shadow-lg lg:shadow-none transform transition-transform duration-300 ease-in-out gap-4 pr-8 bsds-pl-24 ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
          } top-0 lg:top-18 h-screen lg:h-[calc(100vh-5rem)] flex flex-col`}
        role="complementary"
        aria-label="Menu de navegação lateral"
        onClick={(e) => e.stopPropagation()}
      >

        {/* User Card */}
        <div
          id={userCardId}
          className="bsds-mt-16"
          role="region"
          aria-label="Informações do usuário"
        >
          <BsdsCard className="bg-background-primary rounded-md bsds-p-8 border border-gray-100/20 shadow-none" platform="web">
            <div className="flex items-center gap-2 mb-4">
              <div className="flex-1">
                <h6 className="text-sm font-semibold bsds-mb-4" aria-label={`Nome do usuário: ${userName}`}>
                  {userName}
                </h6>
                <h6 className="text-[10px] font-semibold text-text-color/80" role="status" aria-label={`Função: ${roleDisplay || '—'}`}>
                  {roleDisplay || '—'}
                </h6>
              </div>
            </div>
            {hasSelectedPolicy && (
              <div className="space-y-1">
                <div className="flex flex-row gap-1.5">
                  <div
                    className="flex bg-green-700 h-6 w-[100px] rounded-sm p-1 items-center justify-center"
                    role="status"
                    aria-label={empresaLabelFull || 'Nome da empresa'}
                  >
                    <span
                      className="text-xs font-medium text-white block text-center truncate max-w-full"
                      title={empresaLabelFull || undefined}
                    >
                      {empresaLabelShort}
                    </span>
                  </div>
                  <div
                    className="flex bg-green-700 h-6 w-[140px] rounded-sm p-1 items-center justify-center"
                    role="status"
                    aria-label={`Número da apólice: ${apoliceNumeroFull}`}
                  >
                    <span
                      className="text-xs font-medium text-white block text-center truncate max-w-full"
                      title={apoliceNumeroFull || undefined}
                    >
                      Apólice {apoliceNumeroShort}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </BsdsCard>
        </div>

        {/* Menu Items */}
        <nav
          className="flex-1 overflow-y-auto custom-scrollbar"
          role="navigation"
          aria-label="Menu principal de navegação"
        >
          <div className="flex flex-col mr-2" role="list">
            {visibleMenuItems.map((item) => {
              const isSelected = selectedItem === item.id;
              const isDisabled = item.disabled === true || (item.openWebVida === true && isOpeningWebVida);
              return (
                <React.Fragment key={item.id}>
                  <button
                    onClick={() => handleMenuItemClick(item, isDisabled)}
                    className={`group flex items-center gap-4 py-4 rounded-md transition-all duration-200 font-normal relative w-full text-left overflow-hidden my-2 ${isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : 'cursor-pointer'
                      } ${isSelected
                        ? 'px-4 shadow-custom-border'
                        : ' hover:bg-gray-50/70 hover:px-4'
                      }`}
                    role="listitem"
                    aria-label={isDisabled
                      ? `${item.name} (desabilitado)`
                      : isSelected
                        ? `${item.name} (página atual)`
                        : `Ir para ${item.name}`}
                    aria-current={isSelected ? 'page' : undefined}
                    aria-disabled={isDisabled}
                    disabled={isDisabled}
                  >
                    {/* Sidebar */}
                    <div
                      className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-md transition-colors duration-200 ${isSelected
                        ? 'bg-primary-500'
                        : 'bg-transparent group-hover:bg-primary-500'
                        }`}
                      aria-hidden="true"
                    />
                    {/* Effect of filling from left to right */}
                    <div
                      className={`absolute inset-0 left-1 bg-gray-50 transition-transform duration-300 ease-out origin-left ${isSelected
                        ? 'scale-x-100'
                        : 'scale-x-0'
                        }`}
                      aria-hidden="true"
                    />
                    <span
                      className="relative z-10 flex items-center justify-center w-5 h-5 transition-colors duration-200"
                      aria-hidden="true"
                    >
                      {item.icon}
                    </span>
                    <span className="relative z-10 transition-colors duration-200">
                      {item.name}
                    </span>
                  </button>
                  {item.id === 6 && (
                    <Visibility isActive={false}>
                      <div
                        className="w-full mx-auto h-0.5 bg-primary-600 my-4"
                        role="separator"
                        aria-orientation="horizontal"
                        aria-hidden="true"
                      />
                    </Visibility>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </nav>

        {/* Logout Button and Footer */}
        <div className="" role="region" aria-label="Ações e informações do sidebar">
          {user && (
            <div className="">
              <button
                onClick={handleLogoutClick}
                className="group w-full flex items-center gap-4 py-4 rounded-md hover:bg-gray-50 hover:px-4 font-normal relative transition-all duration-200 my-2"
                aria-label="Sair do sistema"
              >
                <div
                  className="absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-primary-500 rounded-l-md transition-colors duration-200"
                  aria-hidden="true"
                />
                <div
                  className="absolute inset-0 left-1 bg-gray-50 transition-transform duration-300 ease-out origin-left scale-x-0 group-hover:scale-x-100"
                  aria-hidden="true"
                />
                <span className="relative z-10 flex items-center justify-center w-5 h-5 transition-colors duration-200">
                  <BsdsIcon icon={iconNavSair} className="w-4 h-4" aria-hidden="true" />
                </span>
                <span className="relative z-10 transition-colors duration-200">
                  Sair
                </span>
              </button>
            </div>
          )}

          {/* Sidebar Footer */}
          <div className="text-center" role="contentinfo" aria-label={`Informações da versão ${packageJson.version}`}>
            <div className="text-xs font-normal text-blue-600 mb-4 mt-2">
              v{packageJson.version}
            </div>
          </div>
        </div>
      </aside>

      {/* Logout confirmation modal */}
      <SystemMessageConfirmation
        type="confirmation"
        isOpen={showLogoutModal}
        isLoading={isLoggingOut}
        title="Confirmar Logout"
        message="Tem certeza que deseja sair do sistema?"
        confirmText="Sair"
        cancelText="Cancelar"
        confirmButtonVariant="danger"
        onConfirm={handleConfirmLogout}
        onCancel={handleCancelLogout}
      />

      <SystemMessage
        message="Selecione uma apólice para acessar este produto."
        type={SystemMessageType.WARNING}
        show={showSelectPolicyMessage}
        onClick={() => setShowSelectPolicyMessage(false)}
      />

      <SystemMessage
        message={webVidaErrorMessage}
        type={SystemMessageType.ERROR}
        show={Boolean(webVidaErrorMessage)}
        onClick={() => setWebVidaErrorMessage('')}
      />
    </>
  );
};

export default Sidebar;
